import React, { useEffect, useState, Fragment } from "react";
import { Card, CardBody, CardHeader, Col, Table } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { getStockItemsList } from "./StockService";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getCurrencySymbol } from "../../utils/currencyHelper";

// Dummy static items array (add more as needed)
const staticItems = [
	{
		_id: 1,
		code: "MD-000001",
		brandName: "Crocin",
		manufacturer: "XYZ Labs",
		package: "Box",
		productForm: "Tablet",
		batchNo: "BATCH123",
		manufacturingDate: "2024-01-01",
		expiryDate: "2026-12-31",
		quantity: 10,
		salePrice: 55,
		discount: 5,
		stockQuantity: 20,
		saltName: "Paracetamol",
		stockId: 1,
	},
	{
		_id: 2,
		code: "MD-000002",
		brandName: "Augmentin",
		manufacturer: "ABC Labs",
		package: "Strip",
		productForm: "Tablet",
		batchNo: "BATCH456",
		manufacturingDate: "2025-02-10",
		expiryDate: "2027-01-15",
		quantity: 5,
		salePrice: 110,
		discount: 0,
		stockQuantity: 10,
		saltName: "Amoxicillin",
		stockId: 1,
	},
	{
		_id: 3,
		code: "MD-000003",
		brandName: "Metformin",
		manufacturer: "DEF Labs",
		package: "Box",
		productForm: "Tablet",
		batchNo: "BATCH789",
		manufacturingDate: "2023-11-01",
		expiryDate: "2025-11-30",
		quantity: 20,
		salePrice: 75,
		discount: 2,
		stockQuantity: 30,
		saltName: "Metformin Hydrochloride",
		stockId: 2,
	},
	{
		_id: 4,
		code: "MD-000004",
		brandName: "Cetrizine",
		manufacturer: "GHI Labs",
		package: "Box",
		productForm: "Tablet",
		batchNo: "BATCH321",
		manufacturingDate: "2024-05-20",
		expiryDate: "2026-05-20",
		quantity: 15,
		salePrice: 40,
		discount: 1,
		stockQuantity: 25,
		saltName: "Cetirizine",
		stockId: 2,
	},
	{
		_id: 5,
		code: "MD-000005",
		brandName: "Dolo 650",
		manufacturer: "JK Labs",
		package: "Box",
		productForm: "Tablet",
		batchNo: "BATCH654",
		manufacturingDate: "2025-09-10",
		expiryDate: "2027-09-10",
		quantity: 30,
		salePrice: 60,
		discount: 3,
		stockQuantity: 40,
		saltName: "Paracetamol",
		stockId: 3,
	},
];

const StockItemsList = () => {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const { stkId } = useParams();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const { pharmacyPOS } = usePharmacy()
	const [stockData, setStockData] = useState(staticItems);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [searchValue, setSearchValue] = useState("");

	useEffect(() => {
		getStocks();
	}, [stkId]);

	const getStocks = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			purchaseId: stkId,
			page: page,
			limit: size,
			search: searchValue.trim()
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getStockItemsList(body);
			setItems(response.data.data.stocks || []);
			const totalCount = response.data.data.total || 0;
			setTotalItems(Math.ceil(totalCount / size));
		} catch (error) {
			console.error("Error fetching sales history:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/purchase`);
	}

	const handleViewClick = (id) => {
		history(`${process.env.PUBLIC_URL}/view-purchase-items/${id}`);
	};

	const formatDate = (dateString) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString(undefined, { month: 'short' });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`; // Example: May 07, 2025
	};

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Stock Items List")}
				parent={t("Dashboard")}
				title={t("Stock Items List")}
			/>
			<Col sm="12">
				<Card>
					<CardHeader>
						<div className="d-flex justify-content-end">
							<button className="btn btn-primary" onClick={gotoList}>
								{t("Back")}
							</button>
						</div>
					</CardHeader>
					<CardBody>
						<div className="table-responsive rounded">
							<Table className="table table-bordered table-hover table-striped mb-0">
								<thead>
									<tr className="text-center align-middle">
										<th>#</th>
										<th>{t("Code")}</th>
										<th className="text-nowrap">{t("Product Name")}</th>
										<th>{t("Batch Number")}</th>
										<th>{t("Expiry Date")}</th>
										<th>{t("Item Quantity")}</th>
										<th>{t("Unit Purchase Price")}</th>
										<th>{t("Unit Sale Price")}</th>
										<th>{t("Action")}</th>
									</tr>
								</thead>
								<tbody>
									{items.length > 0 ? (
										items.map((item, idx) => (
											<tr key={item._id} className="align-middle text-center">
												<td>{idx + 1}</td>
												<td className="text-nowrap">{item.code}</td>
												<td className="text-nowrap">{item.brandName}</td>
												<td className="text-nowrap">{item.batchNumber}</td>
												<td className="text-nowrap">{formatDate(item.expiryDate)}</td>
												<td>{item.totalMedicine}</td>
												<td>{getCurrencySymbol()} {item.unitPrice}</td>
												<td>{getCurrencySymbol()} {item.unitPricePerMedicine}</td>
												<td>
													<OverlayTrigger
														placement="top"
														overlay={<Tooltip id={`tooltip-view-${item?._id}`}>{t("View")}</Tooltip>}
													>
														<button
															className="success-action-button me-1"
															onClick={() => handleViewClick(item?._id)}
														>
															<i
																className="fa fa-eye"
																style={{ fontSize: "18px" }}
															></i>
														</button>
													</OverlayTrigger>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="15" className="text-center p-3 text-muted">
												{t("No items found.")}
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>
					</CardBody>
				</Card>
			</Col>
		</Fragment>
	);
};

export default StockItemsList;